/**
 * BulkActions — reusable Export ▼ + Import button group.
 * Hits /api/bulk/{resource}/{template|export|import}.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Download, Upload, ChevronDown, FileSpreadsheet, FileJson, FileText,
  Loader2, X, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkActions({ resource, accent = '#3fa758', writable = false, onImported, label }) {
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const downloadFile = (url, filename) => {
    const a = document.createElement('a');
    a.href = url; a.download = filename || ''; a.target = '_blank';
    document.body.appendChild(a); a.click();
    setTimeout(() => document.body.removeChild(a), 100);
  };

  const exportAs = async (fmt) => {
    setBusy(true); setExportOpen(false);
    try {
      const r = await axios.get(`${API}/bulk/${resource}/export?format=${fmt}`,
        { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: r.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      downloadFile(url, `${resource}.${fmt}`);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    setBusy(true); setExportOpen(false);
    try {
      const r = await axios.get(`${API}/bulk/${resource}/template`,
        { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      downloadFile(url, `${resource}_template.csv`);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally { setBusy(false); }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <div className="relative" ref={exportRef}>
        <button
          data-testid={`bulk-export-${resource}`}
          onClick={() => setExportOpen(o => !o)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-border bg-background hover:bg-muted disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {label || 'Export'} <ChevronDown className="w-3 h-3" />
        </button>
        {exportOpen && (
          <div className="absolute right-0 mt-1 z-30 bg-background border border-border rounded-xl shadow-lg overflow-hidden min-w-[180px]">
            <button onClick={() => exportAs('csv')} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted text-left">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Export as CSV
            </button>
            <button onClick={() => exportAs('json')} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted text-left">
              <FileJson className="w-3.5 h-3.5" /> Export as JSON
            </button>
            <div className="h-px bg-border" />
            <button onClick={downloadTemplate} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted text-left">
              <FileText className="w-3.5 h-3.5" /> Blank import template
            </button>
          </div>
        )}
      </div>

      {writable && (
        <button data-testid={`bulk-import-${resource}`} onClick={() => setImportOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border-2"
          style={{ borderColor: accent, color: accent }}>
          <Upload className="w-3.5 h-3.5" /> Import
        </button>
      )}

      {importOpen && (
        <ImportModal resource={resource} accent={accent}
          onClose={() => setImportOpen(false)}
          onImported={(data) => { setImportOpen(false); onImported?.(data); }} />
      )}
    </div>
  );
}

function ImportModal({ resource, accent, onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(false);

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0]; if (f) setFile(f);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/bulk/${resource}/import?commit=false`, fd, { withCredentials: true });
      setPreview(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/bulk/${resource}/import?commit=true`, fd, { withCredentials: true });
      onImported(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-xl p-5 shadow-2xl space-y-3">
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <h3 className="font-['Outfit'] text-lg font-bold">Import {resource}</h3>
          <button onClick={onClose} disabled={busy}><X className="w-4 h-4" /></button>
        </div>
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5 text-xs text-red-600 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{err}</div>}

        {!preview ? (
          <>
            <div onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)} onDrop={onDrop}
              data-testid="bulk-import-dropzone"
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${drag ? 'border-current bg-current/5' : 'border-border'}`}
              style={drag ? { borderColor: accent, backgroundColor: `${accent}10` } : {}}>
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-semibold">{file ? file.name : 'Drop CSV or JSON file here'}</p>
              <p className="text-xs text-muted-foreground mt-1">or click to choose</p>
              <input ref={fileRef} type="file" accept=".csv,.json,text/csv,application/json" hidden
                onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
              <button onClick={runPreview} disabled={!file || busy} data-testid="bulk-import-preview"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: accent }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Preview changes'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg bg-muted/40 border border-border p-3 text-sm">
              <p className="font-semibold mb-1">{preview.total_rows} rows parsed</p>
              <ul className="text-xs text-muted-foreground space-y-0.5">
                <li>• {preview.creates} new records will be <strong className="text-foreground">created</strong></li>
                <li>• {preview.updates} existing records will be <strong className="text-foreground">updated</strong></li>
              </ul>
            </div>
            {preview.preview && preview.preview.length > 0 && (
              <div className="bg-card border border-border rounded-lg overflow-hidden text-xs">
                <div className="bg-muted/50 px-3 py-1.5 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Preview (first {Math.min(preview.preview.length, 50)})</div>
                <div className="max-h-48 overflow-y-auto">
                  {preview.preview.slice(0, 20).map((p, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b border-border last:border-0">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.action === 'create' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.action}</span>
                      <span className="font-mono text-[10px]">{p.key}</span>
                      <span className="text-muted-foreground truncate flex-1">{p.doc?.title || p.doc?.asset_name || p.doc?.practice_name || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button onClick={() => setPreview(null)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Back</button>
              <button onClick={commit} disabled={busy} data-testid="bulk-import-commit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: accent }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Apply {preview.creates + preview.updates} changes
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
